class TableSession {
    #provider;
    #backend;
    #target;
    #fields = [];
    #keys = new Set();
    /** Current values per demanded key, owned. */
    #state = new Map();
    #stream;
    /** Identifies the open stream's deliveries; bumped when a stream opens or closes. */
    #streamGeneration = 0;
    #lifetime = new DisposableStack();
    constructor(provider, backend, target) {
        this.#provider = provider;
        this.#backend = backend;
        this.#target = target;
        this.#lifetime.defer(() => this.#close());
    }
    setFields(fields) {
        if (this.#lifetime.disposed) {
            return;
        }
        const added = fields.filter((field) => !this.#fields.includes(field));
        const removed = this.#fields.filter((field) => !fields.includes(field));
        this.#fields = [...fields];
        if (added.length === 0 && removed.length === 0) {
            return;
        }
        for (const values of this.#state.values()) {
            for (const field of removed) {
                values.delete(field);
            }
        }
        this.#reportUnavailable(this.#keys, added);
        this.#request();
    }
    changeKeys(added, removed) {
        if (this.#lifetime.disposed || (added.length === 0 && removed.length === 0)) {
            return;
        }
        for (const key of removed) {
            this.#keys.delete(key);
            this.#state.delete(key);
        }
        for (const key of added) {
            this.#keys.add(key);
        }
        this.#reportUnavailable(added, this.#fields);
        this.#request();
    }
    replay(key, target) {
        const values = this.#state.get(key);
        for (const field of this.#fields) {
            const value = currentValue(values, field);
            if (value === undefined) {
                target.unavailable(field);
            }
            else {
                target.value(field, value.value);
            }
        }
    }
    [Symbol.dispose]() {
        this.#lifetime.dispose();
    }
    #reportUnavailable(keys, fields) {
        if (fields.length === 0) {
            return;
        }
        let open = false;
        for (const key of keys) {
            if (!open) {
                open = true;
                this.#target.begin();
            }
            for (const field of fields) {
                this.#target.unavailable(key, field);
            }
        }
        if (open) {
            this.#target.end();
        }
    }
    /** Sends the current demand to this session's table, opening or releasing it as needed. */
    #request() {
        if (this.#keys.size === 0 || this.#fields.length === 0) {
            this.#close();
            return;
        }
        if (this.#stream === undefined) {
            this.#provider.tablesOpened += 1;
            this.#stream = this.#openStream();
        }
        this.#provider.requests += 1;
        this.#stream.request({ keys: [...this.#keys], fields: [...this.#fields] });
    }
    #openStream() {
        let open = false;
        // Deliveries from a released stream are discarded by generation.
        this.#streamGeneration += 1;
        const generation = this.#streamGeneration;
        const current = () => this.#streamGeneration === generation;
        return this.#backend.open({
            begin: () => {
                open = false;
            },
            row: (key, values) => {
                const state = current() && this.#keys.has(key) ? this.#stateFor(key) : undefined;
                if (state === undefined) {
                    return;
                }
                for (const [field, value] of values) {
                    if (!this.#fields.includes(field) || (state.has(field) && Object.is(state.get(field), value))) {
                        continue;
                    }
                    state.set(field, value);
                    if (!open) {
                        open = true;
                        this.#target.begin();
                    }
                    this.#target.value(key, field, value);
                }
            },
            removed: (key) => {
                const state = current() ? this.#state.get(key) : undefined;
                if (state === undefined) {
                    return;
                }
                for (const field of state.keys()) {
                    if (!open) {
                        open = true;
                        this.#target.begin();
                    }
                    this.#target.unavailable(key, field);
                }
                state.clear();
            },
            end: () => {
                if (open) {
                    open = false;
                    this.#target.end();
                }
            },
        });
    }
    #stateFor(key) {
        let state = this.#state.get(key);
        if (state === undefined) {
            state = new Map();
            this.#state.set(key, state);
        }
        return state;
    }
    #close() {
        const stream = this.#stream;
        this.#stream = undefined;
        this.#streamGeneration += 1;
        stream?.[Symbol.dispose]();
    }
}
/** A usable current value, boxed so valid `undefined`/`null` values survive. */
function currentValue(state, field) {
    if (state === undefined) {
        return undefined;
    }
    for (const [stateField, value] of state) {
        if (Object.is(stateField, field)) {
            return { value };
        }
    }
    return undefined;
}
export class TableColumnProvider {
    #backend;
    /** Consumer-local tables opened over the provider's lifetime. */
    tablesOpened = 0;
    /** Requests sent to open tables (the first request, then one per net demand change). */
    requests = 0;
    constructor(backend) {
        this.#backend = backend;
    }
    open(target) {
        return new TableSession(this, this.#backend, target);
    }
}
//# sourceMappingURL=tableColumnProvider.js.map